export const NOICalculation = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">The Bottom Line</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Calculating T12 Net Operating Income (NOI)
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            NOI is the single most important number in your T12. It determines your property's value and is the foundation of every buyer's investment analysis.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/20e9cb0d32f91e30833127b649a6b321.jpg"
                alt="Net operating income calculation for apartment building T12"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div>
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 text-white shadow-2xl mb-6">
              <h3 className="text-2xl font-bold mb-6">The apartment sale NOI Formula&nbsp;</h3>
              
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-sm opacity-90 mb-1">Gross Rental Income</div>
                  <div className="text-2xl font-bold">$345,600</div>
                </div>
                
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-add-line text-xl"></i>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-sm opacity-90 mb-1">Other Income</div>
                  <div className="text-2xl font-bold">$15,600</div>
                </div>
                
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-subtract-line text-xl"></i>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-sm opacity-90 mb-1">Vacancy Loss</div>
                  <div className="text-2xl font-bold text-red-300">-$17,280</div>
                </div>
                
                <div className="border-t-2 border-white/30 pt-4">
                  <div className="text-sm opacity-90 mb-1">Effective Gross Income</div>
                  <div className="text-2xl font-bold">$343,920</div>
                </div>
                
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-subtract-line text-xl"></i>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-sm opacity-90 mb-1">Total Operating Expenses</div>
                  <div className="text-2xl font-bold">$160,714</div>
                </div>
                
                <div className="border-t-4 border-white pt-4">
                  <div className="text-sm opacity-90 mb-2">Net Operating Income (NOI)</div>
                  <div className="text-4xl font-bold">$183,206</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <h4 className="font-bold text-gray-900 mb-3">Why NOI Matters</h4>
              <p className="text-gray-700 mb-4">
                NOI represents the property's ability to generate cash flow before debt service. It's the numerator in the cap rate formula that determines your property's value.
              </p>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-sm text-gray-700 mb-2"><strong>Property Value Formula:</strong></div>
                <div className="text-center py-3">
                  <div className="text-2xl font-bold text-gray-900">Value = NOI ÷ Cap Rate</div>
                  <div className="text-sm text-gray-600 mt-2">$183,206 ÷ 6.5% = $2,818,554</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">How NOI Changes Impact Property Value</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 px-4 font-bold text-gray-900">Scenario</th>
                  <th className="text-right py-4 px-4 font-bold text-gray-900">T12 NOI</th>
                  <th className="text-center py-4 px-4 font-bold text-gray-900">Cap Rate</th>
                  <th className="text-right py-4 px-4 font-bold text-gray-900">Property Value</th>
                  <th className="text-right py-4 px-4 font-bold text-gray-900">Value Change</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="bg-red-50">
                  <td className="py-4 px-4">
                    <div className="font-semibold text-gray-900">Weak Performance</div>
                    <div className="text-sm text-gray-600">High vacancy, deferred maintenance</div>
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900">$150,000</td>
                  <td className="text-center py-4 px-4 text-gray-700">6.5%</td>
                  <td className="text-right py-4 px-4 font-bold text-red-600">$2,307,692</td>
                  <td className="text-right py-4 px-4 font-bold text-red-600">-$510,862</td>
                </tr>
                <tr className="bg-yellow-50">
                  <td className="py-4 px-4">
                    <div className="font-semibold text-gray-900">Below Average</div>
                    <div className="text-sm text-gray-600">Some issues, room for improvement</div>
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900">$165,000</td>
                  <td className="text-center py-4 px-4 text-gray-700">6.5%</td>
                  <td className="text-right py-4 px-4 font-bold text-yellow-600">$2,538,462</td>
                  <td className="text-right py-4 px-4 font-bold text-yellow-600">-$280,092</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="py-4 px-4">
                    <div className="font-semibold text-gray-900">Current Performance</div>
                    <div className="text-sm text-gray-600">Your actual T12 NOI</div>
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900">$183,206</td>
                  <td className="text-center py-4 px-4 text-gray-700">6.5%</td>
                  <td className="text-right py-4 px-4 font-bold text-blue-600">$2,818,554</td>
                  <td className="text-right py-4 px-4 font-bold text-gray-900">Baseline</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="py-4 px-4">
                    <div className="font-semibold text-gray-900">Improved Performance</div>
                    <div className="text-sm text-gray-600">Reduced vacancy, controlled expenses</div>
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900">$200,000</td>
                  <td className="text-center py-4 px-4 text-gray-700">6.5%</td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">$3,076,923</td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">+$258,369</td>
                </tr>
                <tr className="bg-green-100">
                  <td className="py-4 px-4">
                    <div className="font-semibold text-gray-900">Strong Performance</div>
                    <div className="text-sm text-gray-600">Optimized operations, high occupancy</div>
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900">$220,000</td>
                  <td className="text-center py-4 px-4 text-gray-700">6.5%</td>
                  <td className="text-right py-4 px-4 font-bold text-green-700">$3,384,615</td>
                  <td className="text-right py-4 px-4 font-bold text-green-700">+$566,061</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 p-6 bg-teal-50 border-l-4 border-teal-500 rounded-r-lg">
            <p className="text-gray-700">
              <strong>Key Insight:</strong> Every $10,000 increase in T12 NOI adds approximately $153,846 to your property value at a 6.5% cap rate. Small improvements in income or expense management can create substantial value.
            </p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-arrow-up-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Increase NOI</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Raise rents to market rates</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Add income sources (parking, pets, storage)</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Reduce vacancy through better marketing</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Improve tenant retention</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-arrow-down-line text-3xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Reduce Expenses</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Negotiate better insurance rates</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Implement water conservation measures</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Energy-efficient lighting and HVAC</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Preventive maintenance to avoid costly repairs</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-line-chart-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Maximize Value</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Document all income and expenses accurately</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Maintain clean financial records</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Time your sale when NOI is strongest</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Prepare 3-5 years of historical data</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};